import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, {
  DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES,
  DOCUMENT_SIGN_SOURCE_MIME_TYPE,
  DocumentSignFileResponse,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UploadDocumentInput {
  envelopeId: string;
  file: File;
  onProgress?: (loaded: number, total: number) => void;
}

export class DocumentUploadError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'DocumentUploadError';
  }
}

/**
 * Compute the lowercase-hex SHA-256 of a file's bytes. The Vault upload pins
 * the checksum at three points (client / row / S3), so the browser must send
 * it on the upload-url request before the presigned URL is even minted.
 */
async function computeSha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Drives the three-step upload: presign → PUT → confirm. Progress is reported
 * via the optional callback so the UI can show a bar without us having to
 * thread XHR plumbing through a bunch of components. Uses XHR rather than
 * fetch because fetch's body streams don't surface upload progress in the
 * browsers we target.
 *
 * Every header in `requiredHeaders` MUST be replayed onto the PUT — the
 * presigned URL signature pins them (Content-Type, Content-Length,
 * x-amz-checksum-sha256, x-amz-tagging); a missing/wrong header fails at S3.
 */
async function putWithProgress(
  uploadUrl: string,
  file: File,
  requiredHeaders: Record<string, string>,
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    // The browser sets Content-Length itself from the body and rejects an
    // explicit setRequestHeader for it; every other signed header is replayed.
    for (const [name, value] of Object.entries(requiredHeaders)) {
      if (name.toLowerCase() === 'content-length') continue;
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onerror = () => reject(new DocumentUploadError('NETWORK', 'Upload network error'));
    xhr.onabort = () => reject(new DocumentUploadError('ABORTED', 'Upload aborted'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new DocumentUploadError('S3_REJECTED', `S3 rejected upload (HTTP ${xhr.status})`));
      }
    };
    xhr.send(file);
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation<DocumentSignFileResponse, Error, UploadDocumentInput>({
    mutationFn: async ({ envelopeId, file, onProgress }) => {
      if (file.type !== DOCUMENT_SIGN_SOURCE_MIME_TYPE) {
        throw new DocumentUploadError('INVALID_MIME', 'Only PDF files are supported');
      }
      if (file.size > DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES) {
        throw new DocumentUploadError(
          'TOO_LARGE',
          `File exceeds the ${DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB limit`,
        );
      }

      // Hash the bytes BEFORE requesting the URL — the checksum is part of
      // what the presigned URL signature commits to.
      const checksumSha256 = await computeSha256Hex(file);

      const presign = await api.createDocumentSignUploadUrl(envelopeId, {
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        checksumSha256,
      });

      await putWithProgress(presign.uploadUrl, file, presign.requiredHeaders, onProgress);

      const result = await api.confirmDocumentSignUpload(envelopeId, {
        fileId: presign.fileId,
        documentId: presign.documentId,
        fileName: file.name,
        checksumSha256,
      });

      return result.file;
    },
    onSuccess: (_data, { envelopeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.files(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
    },
  });
}

export function useDeleteDocumentFile(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      await api.deleteDocumentSignFile(envelopeId, fileId);
      return fileId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.files(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
    },
  });
}

export interface RenameDocumentFileInput {
  fileId: string;
  fileName: string;
}

export function useRenameDocumentFile(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, fileName }: RenameDocumentFileInput) => {
      const { file } = await api.renameDocumentSignFile(envelopeId, fileId, { fileName });
      return file;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.files(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
    },
  });
}
